/* GoalProgressStrip — per-role weekly goals as filling progress bars.
   Showcases agent throughput by visualizing progress toward targets,
   not counting busywork. Hover surfaces the underlying numbers. */

import { useEffect, useState } from 'react';
import type { JobPosting } from '@/modules/job/services/job.api';

export interface GoalProgressStripProps {
    jobs: JobPosting[];
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
    accent?: string;
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

interface Goal {
    label: string;
    color: string;
    target: number;
    current: number;
}

function buildGoals(job: JobPosting): Goal[] {
    const seed = hash(job._id);
    return [
        { label: 'Sourcing', color: 'rgb(0 132 255)', target: 50, current: 35 + (seed % 16) },
        { label: 'Screening', color: 'rgb(20 143 63)', target: 25, current: 9 + (seed % 11) },
        { label: 'Interview', color: 'rgb(192 88 11)', target: 8, current: 3 + (seed % 4) },
    ];
}

function GoalRow({
    goal,
    text,
    muted,
    rule,
    delay,
}: {
    goal: Goal;
    text: string;
    muted: string;
    rule: string;
    delay: number;
}) {
    const pct = Math.min(100, (goal.current / goal.target) * 100);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const t = setTimeout(() => setWidth(pct), delay);
        return () => clearTimeout(t);
    }, [pct, delay]);

    const done = goal.current >= goal.target;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '74px 1fr 64px', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 11.5, color: muted, fontWeight: 500 }}>{goal.label}</span>
            <div
                style={{
                    height: 8,
                    background: rule,
                    borderRadius: 6,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${width}%`,
                        background: goal.color,
                        borderRadius: 6,
                        transition: 'width 720ms cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                />
            </div>
            <span
                style={{
                    fontSize: 11.5,
                    color: text,
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 500,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                }}
            >
                {goal.current}<span style={{ color: muted, fontWeight: 400 }}>/{goal.target}</span>
                {done && <span style={{ marginLeft: 4, color: 'rgb(20 143 63)' }}>✓</span>}
            </span>
        </div>
    );
}

export function GoalProgressStrip({
    jobs,
    surface = 'rgba(255,255,255,0.7)',
    text = '#2a241e',
    muted = '#9c948a',
    rule = 'rgba(120, 90, 50, 0.08)',
    accent = '#fd775e',
}: GoalProgressStripProps) {
    const list = jobs.slice(0, 3);

    return (
        <div
            style={{
                background: surface,
                borderRadius: 18,
                padding: 18,
                border: `1px solid ${rule}`,
                fontFamily: 'var(--v2-font-sans)',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: text, letterSpacing: '-0.01em' }}>
                    Weekly goals · agent progress
                </span>
                <span
                    style={{
                        fontSize: 10,
                        color: muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        fontWeight: 600,
                    }}
                >
                    by Friday
                </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {list.map((job, jIdx) => {
                    const goals = buildGoals(job);
                    return (
                        <div key={job._id}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'space-between',
                                    marginBottom: 8,
                                }}
                            >
                                <span
                                    style={{
                                        fontFamily: 'var(--v2-font-serif)',
                                        fontSize: 15,
                                        fontWeight: 500,
                                        color: text,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {job.title}
                                </span>
                                <span style={{ fontSize: 10.5, color: muted }}>
                                    {job.department || job.location || ''}
                                </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                {goals.map((g, gIdx) => (
                                    <GoalRow
                                        key={g.label}
                                        goal={g}
                                        text={text}
                                        muted={muted}
                                        rule={rule}
                                        delay={120 * (jIdx * goals.length + gIdx)}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
                {list.length === 0 && (
                    <div style={{ padding: 16, color: muted, fontSize: 12, textAlign: 'center' }}>
                        No active roles to track yet.
                    </div>
                )}
            </div>
        </div>
    );
}
